import { useContext, useState } from "react";
import {
  Button,
  CircularProgress,
  IconButton,
  Modal,
  Tooltip,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import toast from "react-hot-toast";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FormatColorTextIcon from "@mui/icons-material/FormatColorText";
import CodeIcon from "@mui/icons-material/Code";
import { downloadJsonfile } from "./EditSmartContract";
import axios from "axios";
import DownloadIcon from "@mui/icons-material/Download";
import { useParams } from "react-router-dom";
import { AppContext } from "../../scripts/context/App";

const STYLES = {
  root: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: 1000,
    minHeight: "20vh",
    bgcolor: "#fff",
    borderRadius: "4px",
    boxShadow: 24,
    display: "flex",
    flexDirection: "column",
  },
  actions: {
    borderTop: "1px solid #d3d3d3",
    display: "flex",
    justifyContent: "flex-end",
    p: 3,
    gap: 1,
  },
};

export default function DeployDetailsModal({ data, handleClose }: any) {
  const { user } = useContext(AppContext)
  let { uuid } = useParams();

  const downloadConstructorParametersJsonFile = async (file_path: string) => {
    try {
      const { data: response } = await axios.get(
        "/deploy/download-constructor-parameters-json-file",
        {
          params: {
            file_path,
          },
        }
      );

      const string = JSON.stringify(response, null, 2);
      const blob = new Blob([string], { type: "application/json" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${file_path.split("/").pop()}`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error: any) {
      console.log(error);

      toast.error(
        error.response?.data?.message ||
        "Error downloading constructor parameters file"
      );
    }
  };

  if (!Object.keys(data.data).length) return <></>;

  return (
    <Modal
      keepMounted
      open={data.status}
      onClose={handleClose}
      aria-labelledby="Deploy Details Modal"
    >
      <Box sx={STYLES.root}>
        <Box sx={{ p: 3, borderBottom: "1px solid #d3d3d3" }}>
          <Typography
            variant="h6"
            component="h2"
            sx={{ wordBreak: "break-all" }}
          >
            “{data.data.deployment_title}” Deployment Details
          </Typography>
        </Box>
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {[
                {
                  label: "Deployment UUID",
                  value: data.data.deployment_uuid,
                  actions: [
                    <Tooltip key="Copy uuid" title="Copy uuid">
                      <IconButton
                        size="small"
                        onClick={() => {
                          navigator.clipboard
                            .writeText(data.data.deployment_uuid)
                            .then(() => {
                              toast.success("Copied to clipboard");
                            });
                        }}
                        aria-label="copy uuid"
                      >
                        <ContentCopyIcon sx={{ fontSize: "20px" }} />
                      </IconButton>
                    </Tooltip>,
                    <Tooltip key="Download ABI" title="Download ABI">
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (!user.permissions.includes("download_json")) return toast.error("You don't have permission to download abi");

                          downloadJsonfile(uuid as string, "abi");
                        }}
                      >
                        <FormatColorTextIcon sx={{ fontSize: "20px" }} />
                      </IconButton>
                    </Tooltip>,
                    <Tooltip key="Download Bytecode" title="Download Bytecode">
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (!user.permissions.includes("download_json")) return toast.error("You don't have permission to download abi");

                          downloadJsonfile(uuid as string, "bytecode");
                        }}
                      >
                        <CodeIcon sx={{ fontSize: "20px" }} />
                      </IconButton>
                    </Tooltip>,
                  ],
                },
                {
                  label: "Smart Contract Address",
                  value: data.data.smart_contract_address,
                  actions: [
                    <Tooltip
                      key="Copy Smart Contract Address"
                      title="Copy Smart Contract Address"
                    >
                      <IconButton
                        size="small"
                        onClick={() => {
                          navigator.clipboard
                            .writeText(data.data.smart_contract_address)
                            .then(() => {
                              toast.success("Copied to clipboard");
                            });
                        }}
                        aria-label="copy Smart Contract Address"
                      >
                        <ContentCopyIcon sx={{ fontSize: "20px" }} />
                      </IconButton>
                    </Tooltip>,
                  ],
                },
                {
                  label: "Owner Address",
                  value: data.data.owner_address,
                  actions: [
                    <Tooltip
                      key="Copy Owner Address"
                      title="Copy Owner Address"
                    >
                      <IconButton
                        size="small"
                        onClick={() => {
                          navigator.clipboard
                            .writeText(data.data.owner_address)
                            .then(() => {
                              toast.success("Copied to clipboard");
                            });
                        }}
                        aria-label="copy Owner Address"
                      >
                        <ContentCopyIcon sx={{ fontSize: "20px" }} />
                      </IconButton>
                    </Tooltip>,
                  ],
                },
              ].map((item: any) => (
                <Box key={item.label}>
                  <Typography variant="body2">{item.label}</Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: "bold", wordBreak: "break-all" }}
                    >
                      {item.value}
                    </Typography>
                    <Box>{item.actions.map((action: any) => action)}</Box>
                  </Box>
                </Box>
              ))}
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {[
                { label: "Project Name", value: data.data.project_name },
                { label: "Chain Network:", value: data.data.network_chain },
                { label: "Deployed at:", value: data.data.deployed_at },
              ].map((item: any) => (
                <Box key={item.label}>
                  <Typography variant="body2">{item.label}</Typography>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mt: "2px",
                    }}
                  >
                    <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                      {item.value}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {[
                {
                  label: "Deployment Title:",
                  value: data.data.deployment_title,
                },
                {
                  label: "Deployment Status:",
                  value: data.data.deployment_status,
                },
              ].map((item: any) => (
                <Box key={item.label}>
                  <Typography variant="body2">{item.label}</Typography>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mt: "2px",
                    }}
                  >
                    <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                      {item.value}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "3px" }}>
            <Typography variant="h6" sx={{ mt: 3 }}>
              Initial Parameters:
            </Typography>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
              {Object.keys(data.data.initial_parameters).length ? Object.keys(data.data.initial_parameters).map((key: any) => {
                if (data.data.initial_parameters[key].value === "file") {
                  return (
                    <Box key={key}>
                      <Typography variant="body2">{key}</Typography>
                      <Box sx={{ mt: 1 }}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() =>
                            downloadConstructorParametersJsonFile(
                              data.data.initial_parameters[key].path as string
                            )
                          }
                          startIcon={<DownloadIcon />}
                        >
                          Download .json file
                        </Button>
                      </Box>
                    </Box>
                  );
                }
                return (
                  <Box key={key}>
                    <Typography variant="body2">{key}</Typography>
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                        {data.data.initial_parameters[key].value}
                      </Typography>
                    </Box>
                  </Box>
                );
              }) : (
                <Typography variant="body1">No initial parameters</Typography>
              )}
            </Box>
          </Box>
        </Box>
        <Box sx={STYLES.actions}>
          <Button variant="outlined" color="error" onClick={handleClose}>
            Close
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
